import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { proceduresListingActions } from '@store/actions/procedures-listing.actions';
import { catchError, exhaustMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { proceduresListingSelectors } from '@store/selectors/procedures-listing.selectors';
import { EMPTY, filter, map, Observable, of } from 'rxjs';
import { ApiMyProceduresService } from '@core/api/services/my-procedures/api-my-procedures.service';
import { ProceduresListingTab } from '@core/domain/model/procedures/procedures-listing.model';
import { RequestResponse } from '@core/services/request/model/request.model';
import { DraftListingPagedApi, ProcedureListingPagedApi } from '@core/api/model/my-procedures/procedures-listings-api.model';
import { ProcedureListingMapper } from '@core/domain/mappers/procedures/procedure-listing.mapper';
import { HttpErrorResponse } from '@angular/common/http';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { AcDialogService } from '@core/services/dialog/ac-dialog.service';

@Injectable()
export class ProceduresListingEffects {
  fetchData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        proceduresListingActions.setType,
        proceduresListingActions.setPagination,
        proceduresListingActions.setFilters,
        proceduresListingActions.updateList
      ),
      withLatestFrom(
        this._store.select(proceduresListingSelectors.type),
        this._store.select(proceduresListingSelectors.filters),
        this._store.select(proceduresListingSelectors.pagination)
      ),
      filter(([, type]) => type != null),
      switchMap(([, type, filters, paginator]) => {
        const apiFilters = ProcedureListingMapper.domainFilterToApi(paginator, filters);
        let obs: Observable<RequestResponse<ProcedureListingPagedApi | DraftListingPagedApi>>;
        switch (type!) {
          case ProceduresListingTab.ALL:
            obs = this._apiProcedures.fetchAllProcedures(apiFilters);
            break;
          case ProceduresListingTab.DRAFTS:
            obs = this._apiProcedures.fetchDrafts(apiFilters);
            break;
          case ProceduresListingTab.CLOSED:
            obs = this._apiProcedures.fetchClosedProcedures(apiFilters);
            break;
          case ProceduresListingTab.REQUIRED:
            obs = this._apiProcedures.fetchRequiredProcedures(apiFilters);
            break;
          case ProceduresListingTab.PENDING:
            obs = this._apiProcedures.fetchPendingProcedures(apiFilters);
            break;
        }
        return obs!.pipe(
          map((response) => response.data.values),
          map((data) => ProcedureListingMapper.apiDataToDomain(data)),
          map((data) => proceduresListingActions.setData({ data })),
          catchError((err: HttpErrorResponse) => of(proceduresListingActions.setError({ error: errorFromHttpErrorResponse(err) })))
        );
      })
    )
  );

  deleteDraft$ = createEffect(() =>
    this._actions$.pipe(
      ofType(proceduresListingActions.deleteDraft),
      switchMap(({ draftId }) =>
        this._dialog.confirmationDialog('PROCEDURES.DELETE_DRAFT_CONFIRMATION').pipe(
          filter((value) => !!value),
          map(() => draftId)
        )
      ),
      exhaustMap((draftId) =>
        this._apiProcedures.deleteDraft(draftId).pipe(
          catchError(() => EMPTY),
          filter(({ data }) => data),
          map(() => proceduresListingActions.updateList())
        )
      )
    )
  );

  constructor(
    private _actions$: Actions,
    private _store: Store<StoreState>,
    private _apiProcedures: ApiMyProceduresService,
    private _dialog: AcDialogService
  ) {}
}
